import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import { TableSortLabel } from '@material-ui/core'
import { Checkbox, styled } from 'opensolar-ui'
import { useCallback, useMemo } from 'react'
import { useTranslate } from 'react-admin'
import type { HardwareOrderProjectRecord } from '../../type'

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
}))

const StyledTableRow = styled(TableRow)({})

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 500,
  color: theme.palette.grey[900],
  padding: '12px 16px',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  verticalAlign: 'top',
}))

const CheckboxCell = styled(TableCell)(({ theme }) => ({
  padding: '0 0 0 16px',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  width: '56px',
}))

const ActionHeaderCell = styled(TableCell)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  width: '180px',
  minWidth: '180px',
  backgroundColor: theme.palette.grey[100],
  whiteSpace: 'normal',
  verticalAlign: 'top',
}))

const StyledTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
  '&:hover': {
    color: theme.palette.grey[700],
  },
  '&.MuiTableSortLabel-active': {
    color: theme.palette.grey[900],
  },
  '& .MuiTableSortLabel-icon': {
    opacity: 0.5,
    color: theme.palette.grey[500],
  },
  '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
    opacity: 1,
    color: theme.palette.common.black,
  },
}))

type HeadCell = {
  source: string
  label: string
  width: string
  sortable?: boolean
  sortField?: string
}

export const headCells: HeadCell[] = [
  {
    source: 'address',
    label: 'Address',
    width: '25%',
  },
  {
    source: 'contacts__family_name',
    label: 'Contact',
    width: '15%',
  },
  {
    source: 'system',
    label: 'System Option',
    width: '18%',
  },
  {
    source: 'status',
    label: 'Status',
    width: '12%',
    sortable: true,
    sortField: 'has_orders',
  },
  {
    source: 'installation_date',
    label: 'Install Date',
    width: '15%',
    sortable: true,
    sortField: 'installation_date',
  },
]

/** Reused by ProjectTableSkeleton for identical column widths. */
export const MPO_ACTION_COLUMN_STYLE = { width: '180px', minWidth: '180px' } as const

/** Minimum table width so percentage columns stay readable; narrow viewports scroll horizontally. */
export const MPO_TABLE_MIN_WIDTH = 720

type ProjectTableHeadPropsType = {
  data?: HardwareOrderProjectRecord[]
  selectedProjectIds?: number[]
  onSelectAll?: () => void
  onDeselectAll?: () => void
  currentOrdering?: string
  onSort?: (field: string) => void
}

const ProjectTableHead = ({
  data = [],
  selectedProjectIds = [],
  onSelectAll,
  onDeselectAll,
  currentOrdering,
  onSort,
}: ProjectTableHeadPropsType) => {
  const translate = useTranslate()

  const selectableProjectIds = useMemo(() => {
    return data.filter((record) => record.systems && record.systems.length > 0).map((record) => record.id)
  }, [data])

  const allSelected =
    selectableProjectIds.length > 0 && selectableProjectIds.every((id) => selectedProjectIds.includes(id))
  const someSelected = selectableProjectIds.some((id) => selectedProjectIds.includes(id))
  const isIndeterminate = someSelected && !allSelected

  const handleSelectAllClick = useCallback(() => {
    if (someSelected) {
      onDeselectAll?.()
    } else {
      onSelectAll?.()
    }
  }, [someSelected, onSelectAll, onDeselectAll])

  const getSortDirection = (sortField: string): 'asc' | 'desc' => {
    if (!currentOrdering) return 'asc'
    const isDesc = currentOrdering.startsWith('-')
    const field = isDesc ? currentOrdering.slice(1) : currentOrdering
    if (field === sortField) {
      return isDesc ? 'desc' : 'asc'
    }
    return 'asc'
  }

  const isSortActive = (sortField: string): boolean => {
    if (!currentOrdering) return false
    const field = currentOrdering.startsWith('-') ? currentOrdering.slice(1) : currentOrdering
    return field === sortField
  }

  const handleSort = (sortField: string) => {
    if (!onSort) return
    onSort(sortField)
  }

  return (
    <StyledTableHead>
      <StyledTableRow>
        {/* Checkboxes removed from multi-project order table
        <CheckboxCell>
          <Checkbox
            className="select-all"
            color="primary"
            checked={allSelected}
            indeterminate={isIndeterminate}
            disabled={selectableProjectIds.length === 0}
            onChange={handleSelectAllClick}
          />
        </CheckboxCell>
        */}
        {headCells.map((headCell) => (
          <StyledTableCell key={headCell.source} variant="head" style={{ width: headCell.width }}>
            {headCell.sortable && headCell.sortField ? (
              <StyledTableSortLabel
                active={isSortActive(headCell.sortField)}
                direction={getSortDirection(headCell.sortField)}
                onClick={() => handleSort(headCell.sortField!)}
              >
                {translate(headCell.label)}
              </StyledTableSortLabel>
            ) : (
              translate(headCell.label)
            )}
          </StyledTableCell>
        ))}
        <ActionHeaderCell />
      </StyledTableRow>
    </StyledTableHead>
  )
}

export default ProjectTableHead
